/**
 * Phrasing (and severity) for the warning codes the bulk-invite preview returns.
 *
 * The proxy sends CODES, not sentences — `lib/taFirstInterviews.js` decides what
 * is wrong, this file decides how the recruiter reads it. Kept out of
 * BulkInviteModal.tsx so that file exports components only (react-refresh).
 */
import type { FirstInvitePreviewCandidate } from '../../services/firstInviteApi';

const WARNING_TEXT: Record<string, string> = {
  'no-candidate-email': 'no email address in Lever — cannot be invited',
  'no-link': 'this stage has no interview link, so the email has no booking URL',
  'role-closed': 'this role is closed in Lever, so its interview links take no new bookings',
  'no-booking-link': 'the interview link produced no booking URL',
  'no-interviewers': 'the interview link has no interviewers',
  'template-not-authored': 'generic template — nobody wrote this stage’s invite email',
  'not-in-first-stage': 'moved out of the first-interview stages since the board loaded',
};

/** Prefixed codes carry the offending token names: "empty:duration,roleTitle". */
export function warningText(code: string): string {
  if (WARNING_TEXT[code]) return WARNING_TEXT[code];
  const [kind, tokens = ''] = code.split(':');
  const list = tokens
    .split(',')
    .filter(Boolean)
    .map((t) => `{${t}}`)
    .join(', ');
  if (kind === 'unresolved') return `${list} stayed unfilled and would be sent as raw text`;
  if (kind === 'empty-critical') return `${list} resolved to nothing`;
  if (kind === 'empty') return `${list} is empty`;
  return code;
}

/**
 * Which warnings mean "do not send this without looking".
 *
 * Mirrors isBlockingWarning in lib/taFirstInterviews.js. Duplicated rather than
 * shipped from the server because it drives the UI's default state — an
 * unreachable proxy must not silently pre-tick a broken invitation.
 */
export function isBlockingWarning(code: string): boolean {
  return (
    code === 'no-candidate-email' ||
    code === 'no-link' ||
    code === 'role-closed' ||
    code === 'no-booking-link' ||
    code === 'not-in-first-stage' ||
    code.startsWith('unresolved:') ||
    code.startsWith('empty-critical:')
  );
}

export const hasBlockingWarning = (c: FirstInvitePreviewCandidate): boolean =>
  (c.warnings || []).some(isBlockingWarning);
